import React, { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trip } from '@/types';
import { Box, Container, Tabs, Tab } from '@mui/material';
import { useI18n } from '@/hooks/useI18n';
import TripOverviewPage from './pages/TripOverviewPage';
import TripTravellersPage from './pages/TripTravellersPage';
import TripPendingSignUpsPage from './pages/TripPendingSignUpsPage';
import TripApartmentsPage from './pages/TripApartmentsPage';
import TripSkiPassesPage from './pages/TripSkiPassesPage';
import TripApartmentTypesPage from './pages/TripApartmentTypesPage';
import TripTransportationOptionsPage from './pages/TripTransportationOptionsPage';
import TripBillsPage from './pages/TripBillsPage';

interface TripDashboardProps {
  initialTab?: string;
  trip: Trip;
}

const TripDashboard: React.FC<TripDashboardProps> = ({ initialTab, trip }) => {
  const { tripId, tab } = useParams<{ tripId: string; tab?: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();

  const currentTab = tab || initialTab || 'overview';

  const setActiveTab = useCallback((newTab: string) => {
    navigate(`/trip/${tripId}/${newTab}`);
  }, [navigate, tripId]);

  const tabs = [
    { id: 'overview', label: `📊 ${t('trip.dashboard.tabs.overview')}` },
    { id: 'travellers', label: `👥 ${t('trip.dashboard.tabs.travellers')}` },
    { id: 'pending-signups', label: `📝 ${t('trip.dashboard.tabs.signUps')}` },
    { id: 'apartments', label: `🏠 ${t('trip.dashboard.tabs.apartments')}` },
    { id: 'skipasses', label: `🚠 ${t('trip.dashboard.tabs.skiPasses')}` },
    { id: 'apartment-types', label: `🏡 ${t('trip.dashboard.tabs.apartmentTypes')}` },
    { id: 'transportation-options', label: `🚗 ${t('trip.dashboard.tabs.transportationOptions')}` },
    { id: 'bills', label: `🧾 ${t('trip.dashboard.tabs.bills')}` },
  ];

  const currentTabIndex = tabs.findIndex(tab => tab.id === currentTab);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const newTab = tabs[newValue].id;
    setActiveTab(newTab);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ backgroundColor: 'background.paper', boxShadow: 1, borderBottom: 1, borderColor: 'divider', position: 'sticky', top: 0, zIndex: 30 }}>
        <Container maxWidth="xl">
          <Tabs
            value={currentTabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label={t('trip.dashboard.tabsAriaLabel')}
            sx={{
              minHeight: 48,
              '& .MuiTab-root': {
                minHeight: 48,
                fontSize: '0.875rem',
                fontWeight: 500
              }
            }}
          >
            {tabs.map((tabItem) => (
              <Tab
                key={tabItem.id}
                label={tabItem.label}
                id={`trip-tab-${tabItem.id}`}
                aria-controls={`trip-tabpanel-${tabItem.id}`}
              />
            ))}
          </Tabs>
        </Container>
      </Box>
      
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {currentTab === 'overview' && <TripOverviewPage trip={trip} />}
          {currentTab === 'travellers' && <TripTravellersPage trip={trip} />}
          {currentTab === 'pending-signups' && <TripPendingSignUpsPage trip={trip} />}
          {currentTab === 'apartments' && <TripApartmentsPage trip={trip} />}
          {currentTab === 'skipasses' && <TripSkiPassesPage trip={trip} />}
          {currentTab === 'apartment-types' && <TripApartmentTypesPage trip={trip} />}
          {currentTab === 'transportation-options' && <TripTransportationOptionsPage trip={trip} />}
          {currentTab === 'bills' && <TripBillsPage trip={trip} />}
        </Container>
      </Box>
    </Box>
  );
};

export default TripDashboard;
